import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import TextInput from '../../components/ui/TextInput';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import { Switch } from '../../components/ui/Switch';
import { Select } from '../../components/ui/Select';
import NumberInput from '../../components/ui/NumberInput';
import { Textarea } from '../../components/ui/Textarea';
import { useApplicationSettings, ApplicationSettings, useCompanyProfile, updateCompanyProfile, uploadCompanyLogo, useCompanyLogoBlobUrl } from './useData';
import { useSettings } from '../../contexts/SettingsContext';
import { APP_CONFIG } from '../../config/appConfig';
import api from '../../services/api';
import { cn } from '../../lib/utils';
import Card from '../../components/ui/Card';
import { Tabs, TabsContent } from '../../components/ui/Tabs';
import { Building2, Cog, LayoutDashboard, Sparkles, Table2 } from 'lucide-react';
import SettingsHubPanel from '../../components/SettingsHubPanel';
import type { EmbeddedInConfigurationHub } from '../configuration-settings/models';

export type ApplicationSettingsFormProps = EmbeddedInConfigurationHub;

const defaultDashboardGrid = APP_CONFIG.dashboard;

const SETTINGS_SECTION_TABS = [
  { id: 'features', label: 'Features', hint: 'Access & capabilities', Icon: Sparkles },
  { id: 'general', label: 'General', hint: 'API & app identity', Icon: Cog },
  { id: 'company', label: 'Company', hint: 'Logo & letterhead', Icon: Building2 },
  { id: 'tables', label: 'Tables', hint: 'Defaults & density', Icon: Table2 },
  { id: 'dashboard', label: 'Dashboard', hint: 'Grid & widgets', Icon: LayoutDashboard },
] as const;

const ApplicationSettingsForm: React.FC<ApplicationSettingsFormProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const isAdmin = user?.role === 'ADMIN';
  const featureRowClass = cn(
    'flex items-center justify-between gap-4 rounded-xl border p-4',
    colors.border,
  );
  
  const { applicationSettings, isLoading: loadingApp, mutate: mutateApp } = useApplicationSettings();
  const { refreshSettings } = useSettings();

  const [appFormData, setAppFormData] = useState<Partial<ApplicationSettings>>({
    dashboardGridColumns: defaultDashboardGrid.gridColumns,
    dashboardStatCardWidth: defaultDashboardGrid.statCardWidth,
    dashboardListWidgetWidth: defaultDashboardGrid.listWidgetWidth,
    dashboardChartWidth: defaultDashboardGrid.chartWidth,
    dashboardFullWidth: defaultDashboardGrid.fullWidth,
    sidebarStyle: 'classic',
  });
  const [saving, setSaving] = useState(false);
  const [settingsTab, setSettingsTab] = useState('features');

  const { companyProfile, mutate: mutateCompanyProfile } = useCompanyProfile();
  const { logoBlobUrl: companyLogoBlobUrl, loading: companyLogoLoading } = useCompanyLogoBlobUrl(companyProfile?.logoPath);
  const [companyFormData, setCompanyFormData] = useState({
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    signatoryName: '',
  });
  const [savingCompany, setSavingCompany] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const lastSyncedCompanyProfileIdRef = useRef<number | null>(null);

  const handleSettingsTabChange = (value: string) => {
    setSettingsTab(value);
    if (value === 'company' && companyProfile) {
      lastSyncedCompanyProfileIdRef.current = companyProfile.id;
      setCompanyFormData({
        companyName: companyProfile.companyName ?? '',
        companyAddress: companyProfile.companyAddress ?? '',
        companyPhone: companyProfile.companyPhone ?? '',
        companyEmail: companyProfile.companyEmail ?? '',
        signatoryName: companyProfile.signatoryName ?? '',
      });
    }
  };

  useEffect(() => {
    if (!companyProfile) return;
    if (settingsTab === 'company') return;
    lastSyncedCompanyProfileIdRef.current = companyProfile.id;
    setCompanyFormData({
      companyName: companyProfile.companyName ?? '',
      companyAddress: companyProfile.companyAddress ?? '',
      companyPhone: companyProfile.companyPhone ?? '',
      companyEmail: companyProfile.companyEmail ?? '',
      signatoryName: companyProfile.signatoryName ?? '',
    });
  }, [companyProfile, settingsTab]);

  useEffect(() => {
    if (applicationSettings) {
      const num = (v: unknown): number | undefined => {
        if (v === null || v === undefined) return undefined;
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : undefined;
      };
      setAppFormData({
        enableAdvancedSearch: applicationSettings.enableAdvancedSearch,
        enableExport: applicationSettings.enableExport,
        enableDarkMode: applicationSettings.enableDarkMode,
        enableTablePresets: applicationSettings.enableTablePresets,
        enableConditionalFormatting: applicationSettings.enableConditionalFormatting,
        enableFooter: applicationSettings.enableFooter,
        enableAiEmails: applicationSettings.enableAiEmails ?? false,
        compactEmailView: applicationSettings.compactEmailView ?? false,
        apiTimeout: applicationSettings.apiTimeout,
        appName: applicationSettings.appName || '',
        defaultTablePageSize: applicationSettings.defaultTablePageSize,
        defaultTableDensity: applicationSettings.defaultTableDensity,
        pageSizeOptions: applicationSettings.pageSizeOptions || [50, 100, 200, 500],
        dashboardGridColumns: num(applicationSettings.dashboardGridColumns) ?? defaultDashboardGrid.gridColumns,
        dashboardStatCardWidth: num(applicationSettings.dashboardStatCardWidth) ?? defaultDashboardGrid.statCardWidth,
        dashboardListWidgetWidth: num(applicationSettings.dashboardListWidgetWidth) ?? defaultDashboardGrid.listWidgetWidth,
        dashboardChartWidth: num(applicationSettings.dashboardChartWidth) ?? defaultDashboardGrid.chartWidth,
        dashboardFullWidth: num(applicationSettings.dashboardFullWidth) ?? defaultDashboardGrid.fullWidth,
        sidebarStyle: (applicationSettings.sidebarStyle as ApplicationSettings['sidebarStyle']) ?? 'classic',
      });
    }
  }, [applicationSettings]);

  const saveApplicationSettings = async () => {
    setSaving(true);

    try {
      const payload: Record<string, unknown> = { ...appFormData };
      await api.patch('/settings/application', payload);
      await mutateApp();
      refreshSettings();
      showSuccess('Application settings updated successfully');
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to update application settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCompanyProfileSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault?.();
    setSavingCompany(true);
    try {
      const updated = await updateCompanyProfile(companyFormData);
      lastSyncedCompanyProfileIdRef.current = updated.id;
      setCompanyFormData({
        companyName: updated.companyName ?? '',
        companyAddress: updated.companyAddress ?? '',
        companyPhone: updated.companyPhone ?? '',
        companyEmail: updated.companyEmail ?? '',
        signatoryName: updated.signatoryName ?? '',
      });
      await mutateCompanyProfile(updated, false);
      await mutateCompanyProfile();
      showSuccess('Company details updated');
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(msg || 'Failed to update company details');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      await uploadCompanyLogo(file);
      await mutateCompanyProfile();
      showSuccess('Logo uploaded');
      e.target.value = '';
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(msg || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (!isAdmin) {
    if (embedded) {
      return (
        <Card variant="outlined" className="p-6 text-center">
          <p className={cn('text-sm', colors.text)}>
            Application settings are only available to administrators.
          </p>
        </Card>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="text-center">
          <h2 className={cn('mb-2 text-2xl font-bold', colors.text)}>Access denied</h2>
          <p className={cn('text-sm', colors.textMuted)}>
            Application settings are only available to administrators.
          </p>
        </div>
      </div>
    );
  }

  if (loadingApp) {
    if (embedded) {
      return (
        <div className="py-10">
          <LoadingSpinner message="Loading settings…" />
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner message="Loading settings…" size="large" />
      </div>
    );
  }

  const applicationSaveFooter = (
    <div className={cn('flex justify-end border-t pt-6', colors.border)}>
      <Button type="button" variant="default" loading={saving} disabled={saving} onClick={() => void saveApplicationSettings()}>
        Save application settings
      </Button>
    </div>
  );

  const settingsContent = (
        <Tabs value={settingsTab} onValueChange={handleSettingsTabChange}>
          <div
            className={cn(
              'rounded-xl border p-1.5 shadow-sm',
              'bg-gradient-to-b from-slate-50/95 to-slate-100/80 dark:from-slate-900/50 dark:to-slate-950/80',
              colors.border,
            )}
            role="tablist"
            aria-label="Settings sections"
          >
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-5">
                  {SETTINGS_SECTION_TABS.map(({ id, label, hint, Icon }) => {
                    const isActive = settingsTab === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        id={`app-settings-tab-${id}`}
                        onClick={() => handleSettingsTabChange(id)}
                        className={cn(
                          'group flex min-h-[4.25rem] flex-col items-stretch justify-center gap-0.5 rounded-lg px-3 py-2 text-left transition-all duration-200',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 dark:focus-visible:ring-offset-slate-950',
                          isActive
                            ? cn(
                                colors.primary,
                                colors.primaryText,
                                'shadow-md ring-1 ring-black/[0.06] dark:ring-white/10'
                              )
                            : cn(
                                'border border-transparent hover:border-slate-200/80 hover:bg-white/70 dark:hover:border-slate-700 dark:hover:bg-slate-800/60',
                                colors.text
                              )
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <Icon
                            className={cn(
                              'h-4 w-4 shrink-0 transition-opacity',
                              isActive ? 'opacity-100' : 'opacity-60 group-hover:opacity-90'
                            )}
                            strokeWidth={1.75}
                            aria-hidden
                          />
                          <span className="text-sm font-semibold leading-tight tracking-tight">{label}</span>
                        </span>
                        <span
                          className={cn(
                            'line-clamp-2 pl-[1.375rem] text-[11px] leading-snug',
                            isActive ? 'text-white/90' : cn(colors.textMuted, 'group-hover:text-slate-600 dark:group-hover:text-slate-300')
                          )}
                        >
                          {hint}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <TabsContent value="features" className="mt-8 space-y-4">
                <p className={cn('text-xs', colors.textMuted)}>Enable or disable application features for all users.</p>
                <div className="space-y-3">
                  <div className={featureRowClass}>
                    <div className="flex-1">
                      <Label htmlFor="enableAdvancedSearch" className="mb-1">
                        Advanced Search
                      </Label>
                      <p className={`text-xs ${colors.textMuted}`}>
                        Enable advanced search functionality across modules
                      </p>
                    </div>
                    <Switch
                      id="enableAdvancedSearch"
                      checked={appFormData.enableAdvancedSearch ?? true}
                      onChange={(e) => setAppFormData({ ...appFormData, enableAdvancedSearch: e.target.checked })}
                    />
                  </div>

                  <div className={featureRowClass}>
                    <div className="flex-1">
                      <Label htmlFor="enableExport" className="mb-1">
                        Export Functionality
                      </Label>
                      <p className={`text-xs ${colors.textMuted}`}>
                        Enable data export to Excel and other formats
                      </p>
                    </div>
                    <Switch
                      id="enableExport"
                      checked={appFormData.enableExport ?? true}
                      onChange={(e) => setAppFormData({ ...appFormData, enableExport: e.target.checked })}
                    />
                  </div>

                  <div className={featureRowClass}>
                    <div className="flex-1">
                      <Label htmlFor="enableFooter" className="mb-1">
                        Application Footer
                      </Label>
                      <p className={`text-xs ${colors.textMuted}`}>
                        Show or hide the footer at the bottom of the app
                      </p>
                    </div>
                    <Switch
                      id="enableFooter"
                      checked={appFormData.enableFooter ?? true}
                      onChange={(e) => setAppFormData({ ...appFormData, enableFooter: e.target.checked })}
                    />
                  </div>

                  <div className={featureRowClass}>
                    <div className="flex-1">
                      <Label htmlFor="enableAiEmails" className="mb-1">
                        AI email assistance
                      </Label>
                      <p className={`text-xs ${colors.textMuted}`}>
                        Enable AI help in email compose (ChatGPT free or in-app Ollama)
                      </p>
                    </div>
                    <Switch
                      id="enableAiEmails"
                      checked={appFormData.enableAiEmails ?? false}
                      onChange={(e) => setAppFormData({ ...appFormData, enableAiEmails: e.target.checked })}
                    />
                  </div>

                  <div className={featureRowClass}>
                    <div className="flex-1">
                      <Label htmlFor="compactEmailView" className="mb-1">
                        Compact email view
                      </Label>
                      <p className={`text-xs ${colors.textMuted}`}>
                        Use a compact layout when viewing a single email (condensed header, fewer lines)
                      </p>
                    </div>
                    <Switch
                      id="compactEmailView"
                      checked={appFormData.compactEmailView ?? false}
                      onChange={(e) => setAppFormData({ ...appFormData, compactEmailView: e.target.checked })}
                    />
                  </div>

                  <div className={featureRowClass}>
                    <div className="flex-1">
                      <Label htmlFor="enableDarkMode" className="mb-1">
                        Theme mode
                      </Label>
                      <p className={`text-xs ${colors.textMuted}`}>
                        enable Theme mode options
                      </p>
                    </div>
                    <Switch
                      id="enableDarkMode"
                      checked={appFormData.enableDarkMode ?? true}
                      onChange={(e) => setAppFormData({ ...appFormData, enableDarkMode: e.target.checked })}
                    />
                  </div>

                  <div className={featureRowClass}>
                    <div className="flex-1">
                      <Label htmlFor="enableTablePresets" className="mb-1">
                        Table Presets
                      </Label>
                      <p className={`text-xs ${colors.textMuted}`}>
                        Enable table presets functionality
                      </p>
                    </div>
                    <Switch
                      id="enableTablePresets"
                      checked={appFormData.enableTablePresets ?? true}
                      onChange={(e) => setAppFormData({ ...appFormData, enableTablePresets: e.target.checked })}
                    />
                  </div>

                  <div className={featureRowClass}>
                    <div className="flex-1">
                      <Label htmlFor="enableConditionalFormatting" className="mb-1">
                        Conditional Formatting
                      </Label>
                      <p className={`text-xs ${colors.textMuted}`}>
                        Enable conditional formatting functionality
                      </p>
                    </div>
                    <Switch
                      id="enableConditionalFormatting"
                      checked={appFormData.enableConditionalFormatting ?? true}
                      onChange={(e) => setAppFormData({ ...appFormData, enableConditionalFormatting: e.target.checked })}
                    />
                  </div>
                </div>
                {applicationSaveFooter}
              </TabsContent>

              <TabsContent value="general" className="mt-8 space-y-6">
                <p className={cn('text-xs', colors.textMuted)}>API behavior, application name, and navigation layout.</p>
                <div className="space-y-2">
                  <h3 className={cn('text-sm font-semibold', colors.text)}>API</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="apiTimeout" required>
                        API timeout (ms)
                      </Label>
                      <NumberInput
                        id="apiTimeout"
                        name="apiTimeout"
                        value={appFormData.apiTimeout || 30000}
                        onChange={(e) => setAppFormData({ ...appFormData, apiTimeout: e.target.value as number || 30000 })}
                        min={1000}
                        max={300000}
                        required
                        className="mt-1"
                      />
                      <p className={cn('mt-1 text-xs', colors.textMuted)}>1000–300000 milliseconds</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className={cn('text-sm font-semibold', colors.text)}>Application</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="appName">Application name</Label>
                      <TextInput
                        id="appName"
                        name="appName"
                        value={appFormData.appName || ''}
                        onChange={(e) => setAppFormData({ ...appFormData, appName: e.target.value })}
                        placeholder="UMS Application"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sidebarStyle">Sidebar style</Label>
                      <Select
                        id="sidebarStyle"
                        name="sidebarStyle"
                        value={appFormData.sidebarStyle || 'classic'}
                        options={[
                          { value: 'classic', label: 'Classic sidebar (expandable)' },
                          { value: 'rail', label: 'Navigation rail (compact with drawers)' },
                          { value: 'tabs', label: 'Top tabs + contextual sidebar' },
                          { value: 'workspaces', label: 'Workspaces (role-focused sections)' },
                          { value: 'topmenu', label: 'Top menu (horizontal with dropdowns)' },
                        ]}
                        onChange={(e) =>
                          setAppFormData({
                            ...appFormData,
                            sidebarStyle: e.target.value as ApplicationSettings['sidebarStyle'],
                          })
                        }
                        className="mt-1"
                      />
                      <p className={cn('mt-1 text-xs', colors.textMuted)}>
                        Controls the layout of the main navigation sidebar for all users.
                      </p>
                    </div>
                  </div>
                </div>
                {applicationSaveFooter}
              </TabsContent>

              <TabsContent value="company" className="mt-8 space-y-4">
                <p className={cn('text-xs', colors.textMuted)}>
                  Letterhead data and logo for offers and documents.
                </p>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                      <Label htmlFor="companyName">Company name</Label>
                      <TextInput
                        id="companyName"
                        value={companyFormData.companyName}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, companyName: e.target.value })}
                        placeholder="Company name"
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="companyAddress">Address</Label>
                      <Textarea
                        id="companyAddress"
                        value={companyFormData.companyAddress}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, companyAddress: e.target.value })}
                        placeholder="Address"
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="companyPhone">Phone</Label>
                      <TextInput
                        id="companyPhone"
                        value={companyFormData.companyPhone}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, companyPhone: e.target.value })}
                        placeholder="Phone"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="companyEmail">Email</Label>
                      <TextInput
                        id="companyEmail"
                        type="email"
                        value={companyFormData.companyEmail}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, companyEmail: e.target.value })}
                        placeholder="Email"
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="signatoryName">Signatory name</Label>
                      <TextInput
                        id="signatoryName"
                        value={companyFormData.signatoryName}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, signatoryName: e.target.value })}
                        placeholder="Signatory name"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className={cn('border-t pt-4', colors.border)}>
                    <Label className="block mb-2">Company logo</Label>
                    <p className={cn('text-xs mb-2', colors.textMuted)}>
                      Used on offer PDFs and documents. PNG, JPG, WEBP or GIF, max 2 MB.
                    </p>
                    {companyProfile?.logoPath && (
                      <div className="mb-3 flex items-center gap-3">
                        {companyLogoLoading ? (
                          <span className={cn('text-sm', colors.textMuted)}>Loading logo…</span>
                        ) : companyLogoBlobUrl ? (
                          <>
                            <img
                              src={companyLogoBlobUrl}
                              alt="Company logo"
                              className={cn('h-16 rounded border object-contain', colors.border)}
                            />
                            <span className={cn('text-sm', colors.textMuted)}>Current logo</span>
                          </>
                        ) : (
                          <span className={cn('text-sm', colors.textMuted)}>Could not load logo</span>
                        )}
                      </div>
                    )}
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                      className={cn(
                        'block w-full text-sm file:mr-4 file:rounded-lg file:border-0 file:bg-indigo-500/15 file:px-4 file:py-2 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-500/25 dark:file:bg-indigo-400/15 dark:file:text-indigo-200 dark:hover:file:bg-indigo-400/25',
                        colors.textMuted,
                      )}
                    />
                    {uploadingLogo && (
                      <span className={cn('mt-1 block text-sm', colors.textMuted)}>Uploading…</span>
                    )}
                  </div>
                  <Button type="button" disabled={savingCompany} loading={savingCompany} onClick={handleCompanyProfileSubmit}>
                    Save company details
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="tables" className="mt-8 space-y-4">
                <p className={cn('text-xs', colors.textMuted)}>Default list and table behavior for new users.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="defaultTablePageSize" required>
                      Default Page Size
                    </Label>
                    <NumberInput
                      id="defaultTablePageSize"
                      name="defaultTablePageSize"
                      value={appFormData.defaultTablePageSize || 200}
                      onChange={(e) => setAppFormData({ ...appFormData, defaultTablePageSize: e.target.value as number || 200 })}
                      min={10}
                      max={1000}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="defaultTableDensity" required>
                      Default Table Density
                    </Label>
                    <Select
                      value={appFormData.defaultTableDensity || 'normal'}
                      onChange={(e) => setAppFormData({ ...appFormData, defaultTableDensity: e.target.value as any })}
                      className="mt-1"
                      options={[
                        { value: 'compact', label: 'Compact' },
                        { value: 'normal', label: 'Normal' },
                        { value: 'comfortable', label: 'Comfortable' },
                      ]}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="pageSizeOptions" required>
                      Page Size Options
                    </Label>
                    <TextInput
                      id="pageSizeOptions"
                      name="pageSizeOptions"
                      value={appFormData.pageSizeOptions?.join(', ') || '50, 100, 200, 500'}
                      onChange={(e) => {
                        const value = e.target.value;
                        const options = value
                          .split(',')
                          .map((v) => parseInt(v.trim(), 10))
                          .filter((v) => !isNaN(v) && v > 0);
                        setAppFormData({ ...appFormData, pageSizeOptions: options.length > 0 ? options : [50, 100, 200, 500] });
                      }}
                      placeholder="50, 100, 200, 500"
                      className="mt-1"
                    />
                    <p className={`mt-1 text-xs ${colors.textMuted}`}>
                      Comma-separated list of page size options (e.g., 50, 100, 200, 500)
                    </p>
                  </div>
                </div>
                {applicationSaveFooter}
              </TabsContent>

              <TabsContent value="dashboard" className="mt-8 space-y-4">
                <p className={cn('text-xs', colors.textMuted)}>Column units and widget widths per dashboard row.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                  <div>
                    <Label htmlFor="dashboardGridColumns">Grid columns</Label>
                    <NumberInput
                      id="dashboardGridColumns"
                      name="dashboardGridColumns"
                      value={appFormData.dashboardGridColumns ?? defaultDashboardGrid.gridColumns}
                      onChange={(e) => setAppFormData({ ...appFormData, dashboardGridColumns: e.target.value ?? defaultDashboardGrid.gridColumns })}
                      min={4}
                      max={12}
                      className="mt-1"
                    />
                    <p className={`mt-1 text-xs ${colors.textMuted}`}>e.g. 6 → 6 stat, 3 list, 2 chart, 1 full/row</p>
                  </div>
                  <div>
                    <Label htmlFor="dashboardStatCardWidth">Stat card width</Label>
                    <NumberInput
                      id="dashboardStatCardWidth"
                      name="dashboardStatCardWidth"
                      value={appFormData.dashboardStatCardWidth ?? defaultDashboardGrid.statCardWidth}
                      onChange={(e) => setAppFormData({ ...appFormData, dashboardStatCardWidth: e.target.value ?? defaultDashboardGrid.statCardWidth })}
                      min={1}
                      max={12}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dashboardListWidgetWidth">List widget width</Label>
                    <NumberInput
                      id="dashboardListWidgetWidth"
                      name="dashboardListWidgetWidth"
                      value={appFormData.dashboardListWidgetWidth ?? defaultDashboardGrid.listWidgetWidth}
                      onChange={(e) => setAppFormData({ ...appFormData, dashboardListWidgetWidth: e.target.value ?? defaultDashboardGrid.listWidgetWidth })}
                      min={1}
                      max={12}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dashboardChartWidth">Chart width</Label>
                    <NumberInput
                      id="dashboardChartWidth"
                      name="dashboardChartWidth"
                      value={appFormData.dashboardChartWidth ?? defaultDashboardGrid.chartWidth}
                      onChange={(e) => setAppFormData({ ...appFormData, dashboardChartWidth: e.target.value ?? defaultDashboardGrid.chartWidth })}
                      min={1}
                      max={12}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dashboardFullWidth">Full width</Label>
                    <NumberInput
                      id="dashboardFullWidth"
                      name="dashboardFullWidth"
                      value={appFormData.dashboardFullWidth ?? defaultDashboardGrid.fullWidth}
                      onChange={(e) => setAppFormData({ ...appFormData, dashboardFullWidth: e.target.value ?? defaultDashboardGrid.fullWidth })}
                      min={1}
                      max={12}
                      className="mt-1"
                    />
                  </div>
                </div>
                {applicationSaveFooter}
              </TabsContent>
            </Tabs>
  );

  if (embedded) {
    return <div className="space-y-6">{settingsContent}</div>;
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl space-y-6 p-6">
        <SettingsHubPanel
          title="Application settings"
          badge={
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums',
                colors.textMuted,
                'bg-black/[0.06] dark:bg-white/[0.08]',
              )}
            >
              {SETTINGS_SECTION_TABS.length} areas
            </span>
          }
          description={
            <>
              Feature toggles, branding, tables, and dashboard layout. Each tab has its own fields; use{' '}
              <strong className={cn('font-medium', colors.text)}>Save application settings</strong> at the bottom of
              Features, General, Tables, and Dashboard to persist those sections.
            </>
          }
          helpItems={[
            {
              summary: 'Email, SMTP, and templates',
              content: (
                <>
                  Transactional SMTP and notifications live under{' '}
                  <strong className={cn('font-medium', colors.text)}>
                    Configuration → System → Email notifications
                  </strong>
                  . System email templates use their own module in the same group.
                </>
              ),
            },
          ]}
        >
          {settingsContent}
        </SettingsHubPanel>
      </div>
    </div>
  );
};

export default ApplicationSettingsForm;

